import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import GlobalSearch from "./GlobalSearch";
import {
  FiShoppingCart, FiHeart, FiMenu, FiX, FiChevronDown,
  FiUser, FiShoppingBag, FiLogOut, FiPackage, FiHome,
  FiGrid, FiPhone, FiInfo,
} from "react-icons/fi";
import { useCart } from '../../context/CartContext';
import { useContentSettings } from '../../context/ContentSettingsContext';
import { useAuth } from '../../context/AuthContext';
import CartDrawer from '../CartPanel/CartDrawer';
import { fetchCategories } from '../../api/catalog';

/* ─── 2026 Floating Rounded Header ──────────────────────── */
const Header = forwardRef(({ hidden = false, elevated = false }, ref) => {
  const { itemCount } = useCart();
  const { settings } = useContentSettings();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchCategories().then((data) => {
      if (!cancelled) {
        const catArray = Array.isArray(data) ? data : [];
        setAllCategories(catArray);
        setCategories(catArray.filter((c) => c && !c.parentId));
      }
    }).catch(() => { });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const navCategories = useMemo(() => {
    const defaults = ['Fashion', 'Electronics', 'Jewellery', 'Home & Decor', 'Beauty'];
    if (categories.length > 0) {
      return categories.slice(0, 6).map((c) => ({
        id: c.id || c._id,
        name: c.name,
        to: `/ProductListing?category=${encodeURIComponent(c.id || c._id || c.name)}`,
      }));
    }
    return defaults.map((name) => ({
      name,
      to: `/ProductListing?category=${encodeURIComponent(name)}`,
    }));
  }, [categories]);

  const logoUrl = settings?.brandLogoUrl || null;

  return (
    <header ref={ref} className="fixed top-0 left-0 right-0 z-[100] pointer-events-none">
      <div className="pointer-events-auto">

        {/* ══════════════════════════════════════════════════════
            FLOATING MAIN BAR — rounded pill, floating above page
        ══════════════════════════════════════════════════════ */}
        <div className="px-2 sm:px-3 lg:px-4 pt-2 sm:pt-3 lg:pt-4 max-w-7xl mx-auto w-full">
          <motion.div
            initial={false}
            animate={{
              boxShadow: elevated
                ? '0 8px 40px rgba(0,0,0,0.22), 0 2px 12px rgba(0,0,0,0.12)'
                : '0 4px 24px rgba(0,0,0,0.14), 0 1px 6px rgba(0,0,0,0.08)',
            }}
            transition={{ duration: 0.3 }}
            style={{
              background: 'linear-gradient(135deg, #0b4334 0%, #0b4334 50%, #047857 100%)',
              borderRadius: '20px',
              overflow: 'visible',
            }}
          >
            <div className="px-2 sm:px-4 lg:px-5">
              <div className="flex items-center h-[52px] sm:h-[56px] md:h-[58px] lg:h-[62px] gap-1.5 sm:gap-2 md:gap-2.5 lg:gap-3">

                {/* ── Mobile hamburger ── */}
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.88 }}
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden flex-shrink-0 w-8 sm:w-9 h-8 sm:h-9 flex items-center justify-center text-white rounded-2xl hover:bg-white/15 transition-colors"
                  aria-label="Menu"
                >
                  <FiMenu className="text-[18px] sm:text-[20px]" />
                </motion.button>

                {/* ── Logo ── */}
                <Link to="/" className="flex-shrink-0 flex items-center" aria-label="Home">
                  <img
                    src={logoUrl || "/logo.png"}
                    alt="Trozzi"
                    className="h-8 sm:h-10 md:h-11 lg:h-12 w-auto object-contain"
                    loading="eager"
                    style={{
                      mixBlendMode: 'screen',
                      filter: 'brightness(1.15) contrast(1.1)',
                    }}
                  />
                </Link>

                {/* ── Search Bar ── */}
                <div className="flex-1 min-w-0 mx-0.5 sm:mx-2 md:mx-2.5 lg:mx-3">
                  <GlobalSearch />
                </div>

                {/* ── Desktop Actions ── */}
                <div className="hidden lg:flex items-center gap-0.5 lg:gap-1">

                  {/* User Menu */}
                  <div className="relative" ref={userMenuRef}>
                    <motion.button
                      type="button"
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center gap-1.5 lg:gap-2 text-white text-xs sm:text-sm font-semibold px-2.5 lg:px-3 py-1.5 lg:py-2 rounded-2xl hover:bg-white/15 transition-colors"
                    >
                      {/* Avatar circle */}
                      <div
                        className="w-6 lg:w-7 h-6 lg:h-7 rounded-full flex items-center justify-center text-[10px] lg:text-[11px] font-black flex-shrink-0"
                        style={{
                          background: user ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                          border: '1.5px solid rgba(255,255,255,0.35)',
                        }}
                      >
                        {user
                          ? (user.firstName?.[0] || '').toUpperCase()
                          : <FiUser className="text-[12px]" />
                        }
                      </div>
                      <span className="max-w-[60px] lg:max-w-[80px] truncate leading-none hidden sm:inline">
                        {user ? (user.firstName || 'Account') : 'Login'}
                      </span>
                      <FiChevronDown
                        className={`text-[10px] text-white/70 transition-transform duration-200 hidden sm:inline ${isUserMenuOpen ? 'rotate-180' : ''}`}
                      />
                    </motion.button>

                    {/* User Dropdown */}
                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.94, y: -6 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.94, y: -6 }}
                          transition={{ duration: 0.14, ease: [0.4, 0, 0.2, 1] }}
                          className="absolute right-0 top-full mt-2 w-60 z-[200] overflow-hidden"
                          style={{
                            background: '#ffffff',
                            borderRadius: '20px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.16), 0 6px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #f1f5f9',
                          }}
                        >
                          {/* Header section */}
                          {!user ? (
                            <div className="p-4 border-b border-slate-100">
                              <p className="text-[13px] font-bold text-slate-800 mb-0.5">Welcome!</p>
                              <p className="text-[11px] text-slate-400 mb-3">Sign in to your account</p>
                              <Link
                                to="/login"
                                className="block w-full text-center py-2.5 text-[13px] font-bold text-white rounded-2xl transition-all hover:opacity-90"
                                style={{ background: 'linear-gradient(135deg, #0b4334, #065f46)' }}
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                Login
                              </Link>
                              <div className="flex items-center justify-center gap-1 mt-2.5">
                                <span className="text-[11px] text-slate-400">New here?</span>
                                <Link
                                  to="/register"
                                  className="text-[11px] font-bold text-emerald-600 hover:underline"
                                  onClick={() => setIsUserMenuOpen(false)}
                                >
                                  Create account
                                </Link>
                              </div>
                            </div>
                          ) : (
                            <div
                              className="px-4 py-3 border-b border-slate-100"
                              style={{ background: 'linear-gradient(135deg, #eef4ff 0%, #f5f3ff 100%)' }}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-sm flex-shrink-0"
                                  style={{ background: 'linear-gradient(135deg, #0b4334, #065f46)' }}
                                >
                                  {(user.firstName?.[0] || '').toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[13px] font-bold text-slate-900 truncate">
                                    {user.firstName} {user.lastName}
                                  </p>
                                  <p className="text-[11px] text-slate-400 truncate">{user.email}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Menu items */}
                          <div className="p-2">
                            {user ? (
                              <>
                                {[
                                  { to: '/profile', icon: FiUser, label: 'My Profile' },
                                  { to: '/orders', icon: FiShoppingBag, label: 'My Orders' },
                                  { to: '/wishlist', icon: FiHeart, label: 'Wishlist' },
                                  { to: '/order-tracking', icon: FiPackage, label: 'Track Order' },
                                ].map(({ to, icon: Icon, label }) => (
                                  <Link
                                    key={to}
                                    to={to}
                                    onClick={() => setIsUserMenuOpen(false)}
                                    className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 hover:text-emerald-600 rounded-xl transition-colors"
                                  >
                                    <div className="w-7 h-7 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                                      <Icon className="text-slate-500 text-[13px]" />
                                    </div>
                                    {label}
                                  </Link>
                                ))}
                                <div className="border-t border-slate-100 mt-1 pt-1">
                                  <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="flex items-center gap-3 w-full px-3 py-2.5 text-[13px] text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                                  >
                                    <div className="w-7 h-7 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                                      <FiLogOut className="text-rose-500 text-[13px]" />
                                    </div>
                                    Logout
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <Link to="/orders" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 rounded-xl transition-colors">
                                  <div className="w-7 h-7 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                                    <FiShoppingBag className="text-slate-500 text-[13px]" />
                                  </div>
                                  My Orders
                                </Link>
                                <Link to="/wishlist" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 rounded-xl transition-colors">
                                  <div className="w-7 h-7 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                                    <FiHeart className="text-slate-500 text-[13px]" />
                                  </div>
                                  Wishlist
                                </Link>
                              </>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Cart button */}
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.93 }}
                    onClick={() => navigate('/cart')}
                    className="relative flex items-center gap-1.5 lg:gap-2 text-white text-xs lg:text-[13px] font-semibold px-2.5 lg:px-3 py-1.5 lg:py-2 rounded-2xl hover:bg-white/15 transition-colors"
                  >
                    <div className="relative">
                      <FiShoppingCart className="text-[18px] lg:text-[19px]" />
                      <AnimatePresence>
                        {itemCount > 0 && (
                          <motion.span
                            key={itemCount}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                            className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-1 rounded-full text-white text-[8px] lg:text-[9px] font-black flex items-center justify-center leading-none"
                            style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
                          >
                            {itemCount > 99 ? '99+' : itemCount}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                    <span className="hidden md:inline">Cart</span>
                  </motion.button>

                  {/* More link */}
                  <Link
                    to="/about"
                    className="text-white/70 text-xs lg:text-[13px] font-medium px-2 lg:px-3 py-1.5 lg:py-2 rounded-2xl hover:bg-white/15 hover:text-white transition-colors whitespace-nowrap hidden xl:inline-block"
                  >
                    More
                  </Link>
                </div>

                {/* ── Mobile: Cart icon ── */}
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.88 }}
                  onClick={() => navigate('/cart')}
                  className="lg:hidden relative flex-shrink-0 w-8 sm:w-9 h-8 sm:h-9 flex items-center justify-center text-white rounded-2xl hover:bg-white/15 transition-colors"
                >
                  <FiShoppingCart className="text-[18px] sm:text-[20px]" />
                  <AnimatePresence>
                    {itemCount > 0 && (
                      <motion.span
                        key={itemCount}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        className="absolute top-0.5 right-0.5 min-w-[14px] sm:min-w-[15px] h-[14px] sm:h-[15px] px-0.5 rounded-full text-white text-[7px] sm:text-[8px] font-black flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
                      >
                        {itemCount > 99 ? '99+' : itemCount}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>

              </div>
            </div>
          </motion.div>
        </div>

        {/* ══════════════════════════════════════════════════════
            FLOATING CATEGORY NAV — desktop & tablet, separate pill
        ══════════════════════════════════════════════════════ */}
        <div className="hidden lg:block px-2 sm:px-3 lg:px-4 pt-1.5 sm:pt-2 lg:pt-2.5 max-w-7xl mx-auto w-full">
          <div
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderRadius: '16px',
              border: '1px solid rgba(226,232,240,0.8)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
            }}
          >
            <nav className="flex items-center overflow-x-auto scrollbar-hide px-1.5 sm:px-2 lg:px-3 relative" style={{ overflow: 'visible' }}>
              {[{ name: 'Home', to: '/', end: true }, ...navCategories, { name: 'All Products', to: '/ProductListing' }].map((cat) => {
                const catUrl = new URL(cat.to, window.location.origin);
                let isActive = false;
                
                if (cat.end) {
                  isActive = location.pathname === catUrl.pathname && !location.search;
                } else if (location.pathname === catUrl.pathname) {
                  const currentParams = new URLSearchParams(location.search);
                  const targetParams = new URLSearchParams(catUrl.search);
                  
                  let match = true;
                  const targetKeys = Array.from(targetParams.keys());
                  if (targetKeys.length === 0) {
                    match = !location.search;
                  } else {
                    for (const key of targetKeys) {
                      const currentVal = currentParams.get(key);
                      const targetVal = targetParams.get(key);
                      if (!currentVal || currentVal.toLowerCase() !== targetVal.toLowerCase()) {
                        match = false;
                        break;
                      }
                    }
                  }
                  isActive = match;
                }

                // Check for subcategories
                let subs = [];
                if (cat.id) {
                  subs = allCategories.filter(c => {
                    const pid = c.parentId && typeof c.parentId === 'object' ? (c.parentId.id || c.parentId._id) : c.parentId;
                    return String(pid) === String(cat.id);
                  });
                }

                return (
                  <div 
                    key={cat.name} 
                    className="relative flex-shrink-0 mx-0.5 my-1"
                    onMouseEnter={() => setActiveDropdown(cat.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <Link
                      to={cat.to}
                      onClick={(e) => {
                        if (subs.length > 0) {
                          e.preventDefault();
                          setActiveDropdown(activeDropdown === cat.name ? null : cat.name);
                        }
                      }}
                      className={`flex items-center gap-1 px-3 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-[12.5px] font-semibold whitespace-nowrap rounded-xl transition-all duration-200 ${isActive
                        ? 'text-white'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                      }`}
                      style={isActive ? {
                        background: 'linear-gradient(135deg, #0b4334, #065f46)',
                        boxShadow: '0 2px 10px rgba(37,99,235,0.3)',
                      } : {}}
                    >
                      {cat.name}
                      {subs.length > 0 && (
                        <FiChevronDown className={`transition-transform ${activeDropdown === cat.name ? 'rotate-180' : ''}`} />
                      )}
                    </Link>

                    {/* Dropdown Menu */}
                    {subs.length > 0 && activeDropdown === cat.name && (
                      <div 
                        className="absolute left-0 top-full mt-1 w-48 bg-white rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-slate-100 py-2 z-50"
                      >
                        <Link
                          to={cat.to}
                          className="block px-4 py-2 text-sm font-bold text-slate-800 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                        >
                          All {cat.name}
                        </Link>
                        {subs.map(sub => (
                          <Link
                            key={sub.id || sub._id}
                            to={`/ProductListing?category=${encodeURIComponent(cat.id)}&subCategoryId=${encodeURIComponent(sub.id || sub._id)}`}
                            className="block px-4 py-2 text-[13px] text-slate-600 hover:bg-slate-50 hover:text-emerald-600 transition-colors"
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

      </div>

      {/* ══════════════════════════════════════════════════════
          MOBILE DRAWER
      ══════════════════════════════════════════════════════ */}
      {isMobileMenuOpen && typeof document !== 'undefined' && document.body && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[3000]"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Drawer panel */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className="absolute left-0 top-0 bottom-0 w-[290px] overflow-y-auto"
              style={{
                background: '#ffffff',
                boxShadow: '12px 0 48px rgba(0,0,0,0.18)',
                borderRadius: '0 28px 28px 0',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drawer header */}
              <div
                className="px-4 sm:px-5 py-4 sm:py-5 flex items-center justify-between"
                style={{ background: 'linear-gradient(135deg, #0b4334 0%, #0b4334 50%, #047857 100%)' }}
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <img
                    src="/logo.png"
                    alt="Trozzi"
                    className="h-9 sm:h-10 w-auto object-contain flex-shrink-0"
                    style={{
                      mixBlendMode: 'screen',
                      filter: 'brightness(1.15) contrast(1.1)',
                    }}
                  />
                  <div>
                    {user ? (
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div
                          className="w-10 sm:w-11 h-10 sm:h-11 rounded-2xl flex items-center justify-center text-white font-black text-sm sm:text-base flex-shrink-0"
                          style={{ background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.3)' }}
                        >
                          {(user.firstName?.[0] || '').toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-bold text-[13px] sm:text-[14px] truncate">{user.firstName} {user.lastName}</p>
                          <p className="text-emerald-200 text-[10px] sm:text-[11px] truncate max-w-[120px] sm:max-w-[140px]">{user.email}</p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-white font-black text-[15px] sm:text-[16px] mb-1.5 sm:mb-2">Hello, Guest 👋</p>
                        <Link
                          to="/login"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="inline-flex items-center text-[11px] sm:text-[12px] font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-white"
                          style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}
                        >
                          Login / Sign Up
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Nav links */}
              <nav className="py-2.5 sm:py-3 px-2 sm:px-3">
                {[
                  { to: '/', icon: FiHome, label: 'Home' },
                  { to: '/ProductListing', icon: FiGrid, label: 'All Products' },
                  { to: '/cart', icon: FiShoppingCart, label: 'Cart', badge: itemCount },
                  { to: '/wishlist', icon: FiHeart, label: 'Wishlist' },
                  { to: '/orders', icon: FiShoppingBag, label: 'My Orders' },
                  { to: '/profile', icon: FiUser, label: 'My Profile' },
                  { to: '/order-tracking', icon: FiPackage, label: 'Track Order' },
                  { to: '/contact', icon: FiPhone, label: 'Contact Us' },
                  { to: '/about', icon: FiInfo, label: 'About Us' },
                ].map(({ to, icon: Icon, label, badge }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 sm:gap-3 px-2.5 sm:px-3 py-2 sm:py-2.5 text-[12px] sm:text-[13px] font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-2xl transition-colors mb-0.5"
                  >
                    <div
                      className="w-8 sm:w-9 h-8 sm:h-9 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ background: '#f8fafc', border: '1px solid #f1f5f9' }}
                    >
                      <Icon className="text-slate-500 text-[14px] sm:text-[15px]" />
                    </div>
                    <span className="flex-1">{label}</span>
                    {badge > 0 && (
                      <span
                        className="min-w-[20px] h-5 px-1.5 rounded-full text-white text-[9px] sm:text-[10px] font-black flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
                      >
                        {badge}
                      </span>
                    )}
                  </Link>
                ))}

                {/* Categories section */}
                <div className="mt-3 sm:mt-4 mb-1.5 sm:mb-2 px-2.5 sm:px-3">
                  <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Categories</p>
                </div>
                {navCategories.map((cat) => {
                  const catUrl = new URL(cat.to, window.location.origin);
                  let isActive = false;
                  
                  if (cat.end) {
                    isActive = location.pathname === catUrl.pathname && !location.search;
                  } else if (location.pathname === catUrl.pathname) {
                    const currentParams = new URLSearchParams(location.search);
                    const targetParams = new URLSearchParams(catUrl.search);
                    
                    let match = true;
                    const targetKeys = Array.from(targetParams.keys());
                    if (targetKeys.length === 0) {
                      match = !location.search;
                    } else {
                      for (const key of targetKeys) {
                        const currentVal = currentParams.get(key);
                        const targetVal = targetParams.get(key);
                        if (!currentVal || currentVal.toLowerCase() !== targetVal.toLowerCase()) {
                          match = false;
                          break;
                        }
                      }
                    }
                    isActive = match;
                  }
                  
                  return (
                    <Link
                      key={cat.name}
                      to={cat.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-2.5 sm:gap-3 px-2.5 sm:px-3 py-2 sm:py-2.5 text-[12px] sm:text-[13px] font-medium rounded-2xl transition-colors mb-0.5 ${isActive
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'text-slate-600 hover:bg-emerald-50 hover:text-emerald-600'
                        }`}
                    >
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0b4334, #065f46)' }} />
                      {cat.name}
                    </Link>
                  );
                })}

                {/* Logout */}
                {user && (
                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                      className="flex items-center gap-2.5 sm:gap-3 w-full px-2.5 sm:px-3 py-2 sm:py-2.5 text-[12px] sm:text-[13px] font-semibold text-rose-600 hover:bg-rose-50 rounded-2xl transition-colors"
                    >
                      <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-2xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                        <FiLogOut className="text-rose-500 text-[14px] sm:text-[15px]" />
                      </div>
                      Logout
                    </button>
                  </div>
                )}
              </nav>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      <CartDrawer open={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </header>
  );
});

Header.displayName = 'Header';
export default Header;
